import React, { createContext, useState, useEffect } from 'react';

const defaultValues = {
  checkout: {
    id: 0,
    channel_id: 1533294,
    coupons: [],
    line_items: { physical_items: [] },
    cart_amount: 0,
    redirect_urls: {
      cart_url: '',
      checkout_url: '',
      embedded_checkout_url: '',
    },
  },
  addToCart: () => {},
  removeFromCart: () => {},
  updateCartQuantity: () => {},
  addCartMetafield: () => {},
  getCartMetafields: () => {},
  removeCartMetafield: () => {},

  bigCommerceRestClient: () => {},
  bigCommerceClient: () => {},

  addDiscountToCheckout: () => {},
  removeDiscountFromCheckout: () => {},

  changeVatRelief: () => {},
};

export const StoreContext = createContext(defaultValues);

export const StoreProvider = ({ children }) => {
  const [checkout, setCheckout] = useState(defaultValues.checkout);

  const isBrowser = typeof window !== 'undefined';
  const localStorageCheckoutIdKey = `${process.env.GATSBY_BIGCOMMERCE_SHOP_NAME}:checkout-id`;
  const localStorageCustomerMessageKey = `${process.env.GATSBY_BIGCOMMERCE_SHOP_NAME}:customer-message`;

  useEffect(() => {
    initialiseCheckout();
  }, []);

  const getCart = async cartId => {
    try {
      const cart = await fetch('/.netlify/functions/checkoutApi', {
        method: 'POST',
        body: JSON.stringify({
          method: 'GET',
          endpoint:
            `carts/${cartId}?` +
            new URLSearchParams({
              include: 'redirect_urls,line_items.physical_items.options',
            }),
        }),
      })
        .then(res => res.json())
        .then(responseData => responseData);

      return cart;
    } catch (e) {
      console.error(e);
    }
  };

  const addCheckoutConsignment = async cart => {
    try {
      await fetch('/.netlify/functions/checkoutApi', {
        method: 'POST',
        body: JSON.stringify({
          body: [
            {
              address: {
                first_name: '',
                last_name: '',
                email: '',
                company: '',
                address1: '',
                address2: '',
                city: '',
                state_or_province: '',
                state_or_province_code: '',
                country_code: 'GB',
                postal_code: '',
                phone: '',
                custom_fields: [],
              },
              line_items: cart.data.line_items.physical_items.map(
                ({ id, quantity }) => ({
                  item_id: id,
                  quantity: quantity,
                }),
              ),
            },
          ],
          endpoint: `checkouts/${cart.data.id}/consignments`,
        }),
      })
        .then(res => res.json())
        .then(responseData => responseData);
    } catch (e) {
      console.error(e);
    }
  };

  const initialiseCheckout = async () => {
    const cartId = isBrowser
      ? localStorage.getItem(localStorageCheckoutIdKey)
      : null;

    let newCheckout;

    if (cartId) {
      try {
        newCheckout = await getCart(cartId);

        if (!newCheckout.data) {
          localStorage.removeItem(localStorageCheckoutIdKey);
          localStorage.removeItem(localStorageCustomerMessageKey);
        } else {
          setCheckout(newCheckout.data);
        }
      } catch (e) {
        localStorage.removeItem(localStorageCheckoutIdKey);
        localStorage.removeItem(localStorageCustomerMessageKey);
        console.error(e);
      }
    }
  };

  const addToCart = async cartItem => {
    try {
      let cart;
      const cartId = isBrowser
        ? localStorage.getItem(localStorageCheckoutIdKey)
        : null;

      if (cartId) {
        cart = await fetch('/.netlify/functions/checkoutApi', {
          method: 'POST',
          body: JSON.stringify({
            body: cartItem,
            endpoint:
              `carts/${cartId}/items?` +
              new URLSearchParams({
                include: 'redirect_urls',
              }),
          }),
        })
          .then(res => res.json())
          .then(responseData => responseData);
      } else {
        cart = await fetch('/.netlify/functions/checkoutApi', {
          method: 'POST',
          body: JSON.stringify({
            body: {
              ...cartItem,
              channel_id: 1533294,
            },
            endpoint:
              'carts?' +
              new URLSearchParams({
                include: 'redirect_urls',
              }),
          }),
        })
          .then(res => res.json())
          .then(responseData => responseData);

        if (!cart.error) {
          // Recapture cart after checkout update
          cart = await getCart(cart.data.id);

          if (isBrowser && cart.data && cart.data.id) {
            localStorage.setItem(localStorageCheckoutIdKey, cart.data.id);
          }
        }
      }

      !cart.error && setCheckout(cart.data);

      return cart;
    } catch (e) {
      console.error(e);
    }
  };

  const removeFromCart = async cartItem => {
    try {
      const updatedCart = await fetch('/.netlify/functions/checkoutApi', {
        method: 'POST',
        body: JSON.stringify({
          method: 'DELETE',
          endpoint:
            `carts/${checkout.id}/items/${cartItem}?` +
            new URLSearchParams({
              include: 'redirect_urls',
            }),
        }),
      })
        .then(res => res.json())
        .then(responseData => responseData);

      if (!updatedCart.data) {
        // remove cart ID
        localStorage.removeItem(localStorageCheckoutIdKey);
        localStorage.removeItem(localStorageCustomerMessageKey);
        setCheckout(defaultValues.checkout);
      } else {
        setCheckout(updatedCart.data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const updateCartQuantity = async (cartItemId, cartItem) => {
    try {
      const updatedCart = await fetch('/.netlify/functions/checkoutApi', {
        method: 'POST',
        body: JSON.stringify({
          method: 'PUT',
          body: cartItem,
          endpoint:
            `carts/${checkout.id}/items/${cartItemId}?` +
            new URLSearchParams({
              include: 'redirect_urls',
            }),
        }),
      })
        .then(res => res.json())
        .then(responseData => responseData);

      !updatedCart.error && setCheckout(updatedCart.data);

      return updatedCart;
    } catch (e) {
      console.error(e);
    }
  };

  const addCartMetafield = async (cartId, data) => {
    try {
      await fetch('/.netlify/functions/checkoutApi', {
        method: 'POST',
        body: JSON.stringify({
          body: data,
          endpoint: `carts/${cartId}/metafields`,
        }),
      });
    } catch (e) {
      console.error(e);
    }
  };

  const getCartMetafields = async () => {
    try {
      let metafields;
      const cartId = isBrowser
        ? localStorage.getItem(localStorageCheckoutIdKey)
        : null;

      if (cartId) {
        metafields = await fetch('/.netlify/functions/checkoutApi', {
          method: 'POST',
          body: JSON.stringify({
            method: 'GET',
            endpoint: `carts/${cartId}/metafields`,
          }),
        })
          .then(res => res.json())
          .then(responseData => responseData);
      }
      return metafields;
    } catch (e) {
      console.error(e);
    }
  };

  const removeCartMetafield = async metafieldId => {
    try {
      const cartId = isBrowser
        ? localStorage.getItem(localStorageCheckoutIdKey)
        : null;

      if (cartId) {
        await fetch('/.netlify/functions/checkoutApi', {
          method: 'DELETE',
          body: JSON.stringify({
            body: {},
            endpoint: `carts/${cartId}/metafields/${metafieldId}`,
          }),
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const addDiscountToCheckout = async discountCode => {
    try {
      await fetch('/.netlify/functions/checkoutApi', {
        method: 'POST',
        body: JSON.stringify({
          method: 'POST',
          body: { coupon_code: discountCode },
          endpoint: `checkouts/${checkout.id}/coupons`,
        }),
      })
        .then(res => res.json())
        .then(responseData => responseData);

      const cart = await getCart(checkout.id);
      setCheckout(cart.data);
    } catch (e) {
      console.error(e);
    }
  };

  const removeDiscountFromCheckout = async discountCode => {
    try {
      await fetch('/.netlify/functions/checkoutApi', {
        method: 'POST',
        body: JSON.stringify({
          method: 'DELETE',
          endpoint: `checkouts/${checkout.id}/coupons/${discountCode}`,
        }),
      })
        .then(res => res.json())
        .then(responseData => responseData);

      const cart = await getCart(checkout.id);

      setCheckout(cart.data);
    } catch (e) {
      console.error(e);
    }
  };

  const bigCommerceRestClient = async (endpoint, method, body) => {
    const response = await fetch('/.netlify/functions/bigcommerceRest', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        endpoint: endpoint,
        method: method,
        body: body,
      }),
    });
    const data = await response.json();

    return data;
  };

  const bigCommerceClient = async graphql => {
    const response = await fetch('/.netlify/functions/bigcommerce', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(graphql),
    });
    const data = await response.json();

    return data;
  };

  const changeVatRelief = async (customerMessage = undefined) => {
    try {
      // Recreate current cart
      let lineItems = [];
      let updatedCart;

      // Need to add any variant ID's
      checkout.line_items.physical_items.map(item =>
        lineItems.push({
          quantity: item.quantity,
          product_id: item.product_id,
          variant_id: item.variant_id ? item.variant_id : undefined,
          option_selections:
            item.options && item.options.length > 0
              ? item.options.map(({ nameId, valueId }) => ({
                  option_id: nameId,
                  option_value: valueId,
                }))
              : undefined,
        }),
      );

      // Get metafields
      const cartMetafields = await getCartMetafields();

      updatedCart = await fetch('/.netlify/functions/checkoutApi', {
        method: 'POST',
        body: JSON.stringify({
          body: {
            line_items: lineItems,
            channel_id: customerMessage ? 1533294 : 1,
          },
          endpoint:
            'carts?' +
            new URLSearchParams({
              include: 'redirect_urls',
            }),
        }),
      })
        .then(res => res.json())
        .then(responseData => responseData);

      if (!customerMessage) {
        localStorage.removeItem(localStorageCustomerMessageKey);
        await addCheckoutConsignment(updatedCart);
        updatedCart = await getCart(updatedCart.data.id);
      }

      // Set if any metafields exist
      if (cartMetafields.data && cartMetafields.data.length > 0) {
        for (const metafield of cartMetafields.data) {
          await addCartMetafield(updatedCart.data.id, {
            permission_set: metafield.permission_set,
            namespace: metafield.namespace,
            key: metafield.key,
            value: metafield.value,
            description: metafield.description,
          });
        }
      }

      setCheckout(updatedCart.data);

      if (customerMessage) {
        // Set order notes for VAT relief data
        await fetch('/.netlify/functions/checkoutApi', {
          method: 'PUT',
          body: JSON.stringify({
            body: {
              customer_message: customerMessage,
            },
            endpoint: `checkouts/${updatedCart.data.id}`,
          }),
        })
          .then(res => res.json())
          .then(responseData => responseData);
      }

      if (isBrowser && updatedCart.data && updatedCart.data.id) {
        localStorage.setItem(localStorageCheckoutIdKey, updatedCart.data.id);
      }

      return {
        updated: true,
        checkoutUrl: updatedCart.data.redirect_urls.checkout_url,
      };
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <StoreContext.Provider
      value={{
        ...defaultValues,

        checkout,
        addToCart,
        removeFromCart,
        updateCartQuantity,
        addCartMetafield,
        getCartMetafields,
        removeCartMetafield,

        bigCommerceRestClient,
        bigCommerceClient,

        addDiscountToCheckout,
        removeDiscountFromCheckout,

        changeVatRelief,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};
